import { FC } from "react";
import { useGetList } from "react-admin";
import { Typography, Chip, Stack, Link, alpha } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Policy, PolicyStatus, ResourceName } from "@/types";
import { PolicyField } from "@/components/common/PolicyField";
import { Add as AddIcon } from "@mui/icons-material";

interface ActivePolicyCellProps {
  clientId?: string;
}

/**
 * Smart policy indicator for the client list.
 * Shows the active policy (carrier + year), or the most recent expired,
 * or a "No Policy" message. Handles multiple active policies with a "+N" chip.
 */
export const ActivePolicyCell: FC<ActivePolicyCellProps> = ({ clientId }) => {
  const { data: policies = [], isLoading } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: 10 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!clientId, staleTime: 60000 }
  );

  if (isLoading) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }

  if (policies.length === 0) {
    return (
      <Link
        component={RouterLink}
        to="/policies/create"
        underline="none"
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          color: "primary.main",
          fontSize: "0.8125rem",
          fontWeight: 500,
          transition: "all 0.15s ease",
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <AddIcon sx={{ fontSize: 16 }} />
        Add Policy
      </Link>
    );
  }

  // Separate active from non-active
  const activePolicies = policies.filter((p) => p.status === PolicyStatus.ACTIVE);

  if (activePolicies.length === 0) {
    // Show most recent expired/completed
    return <PolicyField policy={policies[0]} maxWidth={120} />;
  }

  if (activePolicies.length === 1) {
    return <PolicyField policy={activePolicies[0]} maxWidth={120} />;
  }

  // Multiple active policies
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <PolicyField policy={activePolicies[0]} maxWidth={100} />
      <Chip
        label={`+${activePolicies.length - 1}`}
        size="small"
        sx={{
          height: 18,
          fontSize: "0.6rem",
          fontWeight: 600,
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
    </Stack>
  );
};
